import type { ReactNode } from "react";
import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { styleWithVars } from "@lib/cssVars";
import type { DataGridRow } from "../columns/types";
import { RangeActionButton } from "../features/range/RangeActionSlot";
import {
    getRangeSegments,
    getRowRangeKey,
    parseRowRangeKey,
    type RangeSegment,
    type RangeSegmentPart,
    type RowRange,
} from "../features/range/rangeKey";
import {
    columnAfterVar,
    columnStartVar,
    columnWidthVar,
} from "../layout/columnVars";
import type { DataGridAtoms } from "../types";
import type { RowLayout } from "./rowLayout";
import { getLiveRow } from "./useRowState";
import styles from "./Grid.module.scss";

interface OutlineBoxProps {
    range: RowRange;
    part: RangeSegmentPart | "seam" | "handle";
    hasStartEdge: boolean;
    hasEndEdge: boolean;
    /** The range is just the active cell, and that cell fails validation. */
    isInvalid: boolean;
    /** The range is just the active cell, and its editor's popup is open. */
    hasPopupEditor: boolean;
    /** Offset and width from the row's start, as CSS lengths. */
    x: string;
    width: string;
    /** On the box holding the range's start edge: the range's width in cells. */
    cellCount?: number;
    children?: ReactNode;
}

const OutlineBox = ({
    range,
    part,
    hasStartEdge,
    hasEndEdge,
    isInvalid,
    hasPopupEditor,
    x,
    width,
    cellCount,
    children,
}: Readonly<OutlineBoxProps>) => (
    <div
        className={styles.rangeOutline}
        data-part={part}
        data-range-top={range.isTop || undefined}
        data-range-bottom={range.isBottom || undefined}
        data-range-start={hasStartEdge || undefined}
        data-range-end={hasEndEdge || undefined}
        data-single={range.isSingle || undefined}
        data-invalid={isInvalid || undefined}
        data-popup-editor={hasPopupEditor || undefined}
        data-range-cells={cellCount}
        style={styleWithVars({ "--dg-range-x": x, "--dg-range-w": width })}
    >
        {children}
    </div>
);

const sumWidths = (columnIds: readonly string[]) => {
    if (columnIds.length === 0) {
        return "0px";
    }
    const widths = columnIds.map((id) => `var(${columnWidthVar(id)})`);
    return `calc(${widths.join(" + ")})`;
};

interface RangeOutlineProps<TData extends RowData> {
    row: DataGridRow<TData>;
    atoms: DataGridAtoms;
    /** `lead` goes before the row's cells (start and center pieces), `trail` after them (end pins). */
    side: "lead" | "trail";
    layout: RowLayout;
    /** `editing.fillHandle`: the square on the bottom-end corner that drags to fill. */
    hasHandle: boolean;
    /** The row's active cell fails validation: its ring takes the invalid color. */
    isActiveCellInvalid: boolean;
}

/**
 * The range (or active cell) outline for one row: a box per pin part over
 * the cells, drawn past the grid lines around it. Subscribes on its own, so
 * moving or growing a range re-renders these boxes and never the row.
 */
export const RangeOutline = <TData extends RowData>({
    row,
    atoms,
    side,
    layout,
    hasHandle,
    isActiveCellInvalid,
}: Readonly<RangeOutlineProps<TData>>) => {
    const { table } = row;
    const range = parseRowRangeKey(
        useSelector(table.atoms.cellSelection, () =>
            getRowRangeKey(table, getLiveRow(row).getDisplayIndex()),
        ),
    );
    const editorColumnId = useSelector(atoms.activeEditor, (editor) =>
        editor?.rowId === row.id ? editor.columnId : null,
    );
    const isEditing = editorColumnId !== null;
    const hasRangeAction = useSelector(
        atoms.rangeAction,
        (action) => action !== null,
    );
    if (!range) {
        return null;
    }
    const isInvalid = range.isSingle && isActiveCellInvalid;
    const hasPopupEditor =
        range.isSingle &&
        editorColumnId !== null &&
        layout.metaById.get(editorColumnId)?.editorPopup !== undefined;
    const columnIds = layout.columnIdsByPart;
    const segments = getRangeSegments({
        columnIds,
        left: range.left,
        right: range.right,
    }).filter(({ part }) => (side === "trail") === (part === "end"));

    const renderBox = (segment: RangeSegment) => {
        const x = sumWidths(segment.offsetColumnIds);
        const width = sumWidths(segment.columnIds);
        const handle =
            hasHandle && !isEditing && range.isBottom && segment.hasEndEdge ? (
                <span
                    key="fill"
                    className={styles.fillHandle}
                    data-dt-fill-handle=""
                />
            ) : null;
        const action =
            hasRangeAction && range.isTop && segment.hasEndEdge ? (
                <RangeActionButton key="action" atoms={atoms} />
            ) : null;
        const corner = handle || action ? [handle, action] : null;
        const box = (
            <OutlineBox
                key={segment.part}
                range={range}
                part={segment.part}
                hasStartEdge={segment.hasStartEdge}
                hasEndEdge={segment.hasEndEdge}
                isInvalid={isInvalid}
                hasPopupEditor={hasPopupEditor}
                x={x}
                width={width}
                cellCount={
                    segment.hasStartEdge
                        ? range.right - range.left + 1
                        : undefined
                }
            >
                {segment.part === "center" ? null : corner}
            </OutlineBox>
        );
        if (segment.part !== "center" || !corner) {
            return box;
        }
        // The center box sits under the pinned cells; its fill handle and
        // range action get a box of their own over them, so neither shows
        // only in part.
        return [
            box,
            <OutlineBox
                key="handle"
                range={range}
                part="handle"
                hasStartEdge={false}
                hasEndEdge={false}
                isInvalid={isInvalid}
                hasPopupEditor={hasPopupEditor}
                x={x}
                width={width}
            >
                {corner}
            </OutlineBox>,
        ];
    };

    // A center box's start edge reaches back over the start pins' last grid
    // line, which the pinned cells paint over; the seam redraws that sliver
    // above them.
    const seamSegment = segments.find(
        ({ part, hasStartEdge, offsetColumnIds }) =>
            part === "center" &&
            hasStartEdge &&
            columnIds.start.length > 0 &&
            offsetColumnIds.length === columnIds.start.length,
    );
    const seam = seamSegment ? (
        <div
            key="seam"
            className={styles.rangeAnchor}
            data-pin="start"
            style={{
                insetInlineStart: `var(${columnStartVar(columnIds.start[0] ?? "")})`,
            }}
        >
            <OutlineBox
                range={range}
                part="seam"
                hasStartEdge
                hasEndEdge={false}
                isInvalid={isInvalid}
                hasPopupEditor={hasPopupEditor}
                x={sumWidths(seamSegment.offsetColumnIds)}
                width="0px"
            />
        </div>
    ) : null;

    const boxes = segments.map((segment) => {
        if (segment.part === "center") {
            return renderBox(segment);
        }
        const ids = columnIds[segment.part];
        const stickyInset =
            segment.part === "start"
                ? { insetInlineStart: `var(${columnStartVar(ids[0] ?? "")})` }
                : {
                      insetInlineEnd: `var(${columnAfterVar(ids.at(-1) ?? "")})`,
                  };
        return (
            <div
                key={segment.part}
                className={styles.rangeAnchor}
                data-pin={segment.part}
                style={stickyInset}
            >
                {renderBox(segment)}
            </div>
        );
    });
    return [...boxes, seam];
};
